import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { NGXLogger } from "ngx-logger";
import { Subject, timer } from "rxjs";
import { PadlockService } from "../padlock/padlock.service";
import { WebsocketService } from "../websocket/websocket.service";
import { Workspace } from "../workspace/state/workspaces.model";
import { selectActiveWorkspace } from "../workspace/state/workspaces.selectors";
import { AsyncMessageHandlerService } from "./async-message-handler.service";
import {
  BusEvent,
  Channel,
  HomingPigeonAction,
  HomingPigeonMessage,
  HomingPigeonRequest,
  HomingPigeonType,
  IQErrorMessage,
} from "./homing-pigeon.model";

const HEARTBEAT_INTERVAL = 30000;

@Injectable({ providedIn: "root" })
export class HomingPigeonService {
  private messages: Subject<BusEvent>;
  private workspace: Workspace;
  private errorListener: MessageListener;
  private tenantChannelListener: MessageListener;

  workspace$ = this.store.select(selectActiveWorkspace);

  constructor(
    private ws: WebsocketService,
    private padlockService: PadlockService,
    private logger: NGXLogger,
    private store: Store<any>,
    private asyncMessageHandlerService: AsyncMessageHandlerService,
  ) {
    // Add Error Listener (filtered on type only - channel and workspace are ignored)
    this.errorListener = new MessageListener((errorMessage: IQErrorMessage) => {
      this.asyncMessageHandlerService.handleError(errorMessage);
    }).onType(HomingPigeonType.ERROR);

    this.tenantChannelListener = new MessageListener(
      (message: HomingPigeonMessage) => {
        this.asyncMessageHandlerService.handleMessage(message);
      },
    ).onChannel(Channel.TENANT);

    this.ws.connect();
    this.messages = this.ws.wsMessages$;

    this.messages.subscribe((message: HomingPigeonMessage) => {
      this.checkListeners(message);
    });

    this.workspace$.subscribe((w) => {
      this.handleActiveWorkspaceChange(w);
    });

    const heartbeatTimer = timer(HEARTBEAT_INTERVAL, HEARTBEAT_INTERVAL);

    heartbeatTimer.subscribe(
      () => {
        this.heartbeat();
      },
      (error) => {
        this.logger.error(`Homing Pigeon websocket heartbeat error: ${error}`);
      },
    );

    this.ws.status.subscribe(() => {
      if (this.ws.isConnected) {
        if (this.workspace) {
          this.join(Channel.TENANT);
        }
      }
    });
  }

  private handleActiveWorkspaceChange(workspace: Workspace) {
    if (this.workspace && workspace && this.workspace.uuid === workspace.uuid) {
      return;
    }

    if (this.workspace) {
      this.leave(Channel.TENANT);
    }

    this.workspace = workspace;

    if (workspace) {
      // Join the workspace channel.
      this.join(Channel.TENANT);
    }
  }

  private heartbeat() {
    if (this.workspace) {
      const action = HomingPigeonAction.HEARTBEAT;
      const authToken = this.padlockService.getAuthToken();
      const request = new HomingPigeonRequest(
        authToken,
        this.workspace.uuid,
        Channel.TENANT,
        action,
      );
      this.ws.send(request);
    }
  }

  private join(channel: string) {
    const action = HomingPigeonAction.JOIN;
    const authToken = this.padlockService.getAuthToken();
    const request = new HomingPigeonRequest(
      authToken,
      this.workspace.uuid,
      channel,
      action,
    );
    this.ws.send(request);
  }

  private leave(channel: string) {
    const action = HomingPigeonAction.LEAVE;
    const authToken = this.padlockService.getAuthToken();
    const request = new HomingPigeonRequest(
      authToken,
      this.workspace.uuid,
      channel,
      action,
    );
    this.ws.send(request);
  }

  private send(channel: string, message: any) {
    const action = HomingPigeonAction.SEND;
    const authToken = this.padlockService.getAuthToken();
    const request = new HomingPigeonRequest(
      authToken,
      this.workspace.uuid,
      channel,
      action,
      message,
    );
    this.ws.send(request);
  }

  private checkListeners(message: HomingPigeonMessage) {
    this.errorListener.check(message);
    this.tenantChannelListener.check(message);
  }
}

export class MessageListener {
  private typeFilter: string = null;
  private channelFilter: string = null;

  constructor(private callback: (message: HomingPigeonMessage) => void) {}

  public onType(type: string): MessageListener {
    // Allow chaining => easiest way to specify any combination of filters
    this.typeFilter = type;
    return this;
  }

  public onChannel(channel: string): MessageListener {
    // Allow chaining => easiest way to specify any combination of filters
    this.channelFilter = channel;
    return this;
  }

  private matches(message: HomingPigeonMessage): boolean {
    if (!message) {
      return false;
    }

    // Check that a message matches all provided filters.
    const typeMatches = this.typeFilter
      ? this.typeFilter === message.type
      : true;
    const channelMatches = this.channelFilter
      ? this.channelFilter === message.channel
      : true;
    return typeMatches && channelMatches;
  }

  public check(message: HomingPigeonMessage) {
    // If the message matches the criteria execute the callback
    if (this.matches(message)) {
      this.callback(message);
    }
  }
}
